import { Controller } from "@hotwired/stimulus"

// Refresh-posts trigger on the autoboost show page.
//
// Click → instant spinner + fast JSON POST → server enqueues
// AutoboostPostRefreshJob and returns ~200ms. Mercure (subscribed via
// <%= turbo_stream_from RealtimeTopic.autoboost(@autoboost) %> on the show page) streams the
// updated sync_controls partial + "Sync complete" toast when the background
// job finishes. The spinner state persists on the local button until the
// turbo-frame is replaced by the broadcast.
export default class extends Controller {
  static targets = ["button", "icon", "label"]
  static values  = { url: String }

  connect() {
    console.log("[autoboost-refresh] connect")
  }

  async start(e) {
    e.preventDefault()
    const url = this.urlValue || (this.hasButtonTarget ? this.buttonTarget.dataset.autoboostRefreshUrlValue : null)
    if (!url) {
      console.warn("[autoboost-refresh] no url — refusing to fire")
      return
    }

    console.log(`[autoboost-refresh] POST ${url}`)
    this.#showSpinner()

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Accept":       "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        credentials: "same-origin",
      })

      if (!res.ok) {
        const body = await res.text().catch(() => "")
        console.error(`[autoboost-refresh] ${res.status} ${body}`)
        this.#resetSpinner()
        // Customer-safe copy — never leak raw HTTP status codes (rule 4).
        this.#toastError("Couldn't refresh — try again in a moment")
        return
      }

      console.log("[autoboost-refresh] queued — waiting for Mercure update")
      // Spinner stays until Mercure replaces the turbo-frame.
    } catch (err) {
      console.error("[autoboost-refresh] fetch failed", err)
      this.#resetSpinner()
      this.#toastError("Couldn't refresh — try again in a moment")
    }
  }

  disconnect() {
    console.log("[autoboost-refresh] disconnect")
  }

  // ── private ────────────────────────────────────────────────────

  #showSpinner() {
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.add("opacity-50", "pointer-events-none")
      this.buttonTarget.disabled = true
    }
    if (this.hasLabelTarget) this.labelTarget.textContent = "Refreshing..."
    if (this.hasIconTarget) {
      this.iconTarget.outerHTML = `
        <svg class="w-3.5 h-3.5 shrink-0 animate-spin" data-autoboost-refresh-target="icon" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
      `
    }
  }

  #resetSpinner() {
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.remove("opacity-50", "pointer-events-none")
      this.buttonTarget.disabled = false
    }
    if (this.hasLabelTarget) this.labelTarget.textContent = "Refresh posts now"
  }

  #toastError(message) {
    const container = document.getElementById("toast_container")
    if (!container) return
    const el = document.createElement("div")
    el.setAttribute("data-controller", "toast")
    el.setAttribute("data-toast-duration-value", "5000")
    el.className = "pointer-events-auto transform translate-x-full opacity-0 transition-all duration-300 ease-out rounded-lg shadow-lg border overflow-hidden bg-red-50 border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-200"
    el.innerHTML = `<div class="flex items-stretch"><div class="flex-1 p-4 text-sm font-medium">${message}</div><button data-action="toast#dismiss" class="px-3 text-current opacity-50 hover:opacity-100 shrink-0 border-l border-current/10" aria-label="Dismiss">×</button></div>`
    container.appendChild(el)
  }
}
